import {useEffect, useCallback, useState} from 'react';
import _ from 'lodash';

/**
 * Wrapper around useContextMenu that uses a configuration object instead of lots of parameters
 * @param outerRef Ref to the element that we will listen to right-clicks on
 * @param width Menu width, usually provided by the MeasureWrapper
 * @param height Menu height, usually provided by the MeasureWrapper
 * @param widthPadding If we want the menu to stay a certain distance from the edge of the screen
 * @param heightPadding If we want the menu to stay a certain distance from the edge of the screen
 * @param onOpen Function that is called after the menu opens. It gets the raw event as parameter
 * @returns {{yPos: number, xPos: number, menu: boolean, close: (function(): void)}}
 */
const useContextMenu2 = (outerRef, width, height, {widthPadding, heightPadding, onOpen}) => {
    return useContextMenu(outerRef, width, height, _.defaultTo(widthPadding, 0), _.defaultTo(heightPadding, 0), onOpen);
};

// we use widthPadding and heightPadding to adjust the position if there are elements at the side or bottom
// of the screen that collide with the tooltip
const useContextMenu = (outerRef, width, height, widthPadding = 0, heightPadding = 0, onOpen) => {
    const [xPos, setXPos] = useState(0);
    const [yPos, setYPos] = useState(0);
    const [menu, showMenu] = useState(false);

    const handleContextMenu = useCallback(
        (event) => {
            event.preventDefault();
            if (outerRef && outerRef.current && outerRef.current.contains(event.target)) {
                setXPos(event.clientX);
                setYPos(event.clientY);
                showMenu(true);
                if (onOpen) onOpen(event);
            } else {
                showMenu(false);
            }
        },
        [showMenu, outerRef, setXPos, setYPos, onOpen],
    );

    const handleClick = useCallback(() => {
        showMenu(false);
    }, [showMenu]);

    useEffect(() => {
        document.addEventListener('click', handleClick);
        document.addEventListener('contextmenu', handleContextMenu);
        return () => {
            document.removeEventListener('click', handleClick);
            document.removeEventListener('contextmenu', handleContextMenu);
        };
    }, [handleClick, handleContextMenu]);

    let computedX = xPos + width + widthPadding > window.innerWidth ? xPos - width : xPos;
    let computedY = yPos + height + heightPadding > window.innerHeight ? yPos - height : yPos;
    return {xPos: computedX, yPos: computedY, menu, close: () => showMenu(false)};
};

export default useContextMenu;

export {useContextMenu, useContextMenu2};
